/**
 * MessageRecipients — who a message is for, and where it stands for each of them (conversation-members spec): under
 * the message, beside its time, as /chats shows a message's delivery state. Only the members a message names receive
 * it. A member that worked on its own thread links it from its answer (the answer's thread link), not from here.
 */
import type { ConversationRecipient } from '@confluent/shared/lib/conversation-addressing';
import { ArrowRight } from 'lucide-react';
import { Chip, type ChipTone } from '../ui/Chip.js';
import { Spinner } from '../ui/Spinner.js';

/** What a recipient's state reads as, for the person looking at the conversation. */
export function recipientState(
  recipient: ConversationRecipient,
  me: string | null,
): { label: string; tone: ChipTone; working: boolean; title?: string } {
  const isMe = recipient.memberType === 'user' && me !== null && String(recipient.memberId) === me;
  switch (recipient.status) {
    case 'running':
      return { label: 'working…', tone: 'blue', working: true };
    case 'answered':
      return { label: 'answered', tone: 'green', working: false };
    case 'read':
      return { label: 'seen', tone: 'neutral', working: false };
    case 'failed':
      return { label: 'could not answer', tone: 'danger', working: false, title: recipient.reason ?? undefined };
    case 'pending':
      if (recipient.replyNeeded)
        return { label: isMe ? 'waiting for you' : 'waiting for a reply', tone: 'warn', working: false };
      return recipient.memberType === 'persona' || recipient.memberType === 'workforce_member'
        ? { label: 'queued', tone: 'neutral', working: false }
        : { label: 'sent', tone: 'neutral', working: false };
  }
}

export function MessageRecipients({ to, me }: { to: readonly ConversationRecipient[]; me: string | null }) {
  if (to.length === 0) return null;
  return (
    <span className="flex flex-wrap items-center gap-1.5 text-xs text-muted" aria-label="Who this message is for">
      <ArrowRight size={12} aria-hidden />
      {to.map((recipient) => {
        const state = recipientState(recipient, me);
        return (
          <span key={recipient.id} className="flex items-center gap-1" title={state.title}>
            <span className="text-body">{recipient.name}</span>
            <Chip
              tone={state.tone}
              icon={state.working ? <Spinner label={`${recipient.name} is working`} /> : undefined}
            >
              {state.label}
            </Chip>
          </span>
        );
      })}
    </span>
  );
}
